import type { WritingBlock } from "./writings";

export type WritingTocItem = {
  id: string;
  text: string;
  depth?: 2 | 3;
};

export function slugifyHeading(text: string): string {
  const slug = text
    .trim()
    .toLowerCase()
    .replace(/[''""]/g, "")
    .replace(/[^a-z0-9\u3040-\u30ff\u3400-\u9fff]+/gi, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  return slug || "section";
}

export function getWritingToc(body: WritingBlock[]): WritingTocItem[] {
  const slugCounts = new Map<string, number>();

  return body.flatMap((block) => {
    if (block.type !== "heading") return [];

    const base = slugifyHeading(block.text);
    const count = slugCounts.get(base) ?? 0;
    slugCounts.set(base, count + 1);

    const id = count === 0 ? base : `${base}-${count + 1}`;
    return [{ id, text: block.text }];
  });
}

export type WritingSectionBlock = {
  block: WritingBlock;
  index: number;
};

export type WritingSection = {
  id: string;
  blocks: WritingSectionBlock[];
};

export function splitWritingIntoSections(
  body: WritingBlock[],
  headingIds: Map<number, string>
): WritingSection[] {
  const sections: WritingSection[] = [];
  let current: WritingSection = { id: "intro", blocks: [] };

  body.forEach((block, index) => {
    if (block.type === "heading") {
      if (current.blocks.length > 0) {
        sections.push(current);
      }
      current = {
        id: headingIds.get(index) ?? `section-${sections.length}`,
        blocks: [{ block, index }],
      };
      return;
    }

    current.blocks.push({ block, index });
  });

  if (current.blocks.length > 0) {
    sections.push(current);
  }

  return sections;
}

export function getHeadingIds(body: WritingBlock[]): Map<number, string> {
  const ids = new Map<number, string>();
  const slugCounts = new Map<string, number>();

  body.forEach((block, index) => {
    if (block.type !== "heading") return;

    const base = slugifyHeading(block.text);
    const count = slugCounts.get(base) ?? 0;
    slugCounts.set(base, count + 1);

    ids.set(index, count === 0 ? base : `${base}-${count + 1}`);
  });

  return ids;
}
